import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Inbox, Archive, Flag,
  MessageCircle, Facebook, Instagram, Trash2,
  CheckCircle2, CornerDownRight,
  Send, RefreshCw, FileText
} from 'lucide-react';
import api from '../../../services/api';

const SocialMediaComments = () => {
  const [groupByPost, setGroupByPost]     = useState(false);
  const [filter, setFilter]               = useState('all');
  const [platform, setPlatform]           = useState('all');
  const [search, setSearch]               = useState('');
  const [selectedComment, setSelectedComment] = useState(null);
  const [replyText, setReplyText]         = useState('');
  const [replyError, setReplyError]       = useState(null);
  const [loading, setLoading]             = useState(true);
  const [syncing, setSyncing]             = useState(false);
  const [sending, setSending]             = useState(false);
  const [comments, setComments]           = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchComments();
    const pollInterval = setInterval(fetchComments, 30000);
    return () => clearInterval(pollInterval);
  }, []);

  const fetchComments = async () => {
    try {
      const res = await api.get('/api/social-media/comments');
      const raw = res.data || [];
      setComments(raw.map(mapComment));
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setLoading(false);
    }
  };

  const mapComment = (c) => ({
    id:        c.id,
    author: {
      name:    c.author_name     || 'Unknown',
      avatar:  c.author_avatar   || null,
      username: c.author_username || ''
    },
    platform:  c.platform,
    text:      c.message,
    timeAgo:   formatTimeAgo(c.platform_created_at || c.created_at),
    timestamp: c.platform_created_at || c.created_at,
    status:    c.status   || 'unread',
    sentiment: c.sentiment || 'neutral',
    post: {
      id:    c.post_id,
      title: c.post_content || 'Post',
      image: c.post_media_url || null
    },
    replies:             c.replies              || [],
    platform_comment_id: c.platform_comment_id
  });

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1)  return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (selectedComment) scrollToBottom();
  }, [selectedComment?.replies]);

  /* ── Actions ── */
  const handleSyncComments = async () => {
    setSyncing(true);
    try {
      await api.post('/api/social-media/comments/sync');
      await fetchComments();
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedComment || sending) return;
    setSending(true);
    setReplyError(null);

    const newReply = {
      id:        Date.now(),
      author:    'Restaurant Admin',
      text:      replyText,
      timestamp: new Date().toISOString()
    };

    const previousReplies  = selectedComment.replies || [];
    const previousComments = comments;
    const sentText = replyText;

    setReplyText('');
    setComments(prev => prev.map(c =>
      c.id === selectedComment.id
        ? { ...c, replies: [...(c.replies || []), newReply], status: 'read' }
        : c
    ));
    setSelectedComment(prev => ({
      ...prev,
      replies: [...previousReplies, newReply],
      status: 'read'
    }));

    try {
      await api.post(`/api/social-media/comments/${selectedComment.id}/reply`, { message: sentText });
    } catch (err) {
      setComments(previousComments);
      setSelectedComment(prev => ({ ...prev, replies: previousReplies }));
      setReplyText(sentText);
      const errMsg = err.response?.data?.error || 'Failed to send reply. Please try again.';
      setReplyError(errMsg);
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    setComments(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
    if (selectedComment?.id === id) {
      setSelectedComment(prev => ({ ...prev, status: newStatus }));
    }
    try {
      await api.put(`/api/social-media/comments/${id}/status`, { status: newStatus });
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const filteredComments = comments.filter(c => {
    if (filter !== 'all' && c.status !== filter) return false;
    if (platform !== 'all' && c.platform !== platform) return false;
    if (search && !c.text?.toLowerCase().includes(search.toLowerCase()) &&
        !c.author.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const groupedComments = React.useMemo(() => {
    if (!groupByPost) return [];
    const groups = {};
    filteredComments.forEach(comment => {
      const postId = comment.post.id || 'unlinked';
      if (!groups[postId]) {
        groups[postId] = {
          post: comment.post,
          comments: []
        };
      }
      groups[postId].comments.push(comment);
    });
    return Object.values(groups);
  }, [filteredComments, groupByPost]);

  const unreadCount = comments.filter(c => c.status === 'unread').length;

  const getSentimentColor = (s) =>
    s === 'positive' ? 'text-green-500' : s === 'negative' ? 'text-red-500' : 'text-yellow-500';
  const getSentimentEmoji = (s) =>
    s === 'positive' ? '😊' : s === 'negative' ? '😟' : '😐';

  // Inner row component to avoid repeat definitions
  const CommentCard = ({ comment, showPostInfo = false }) => {
    const isSelected = selectedComment?.id === comment.id;
    return (
      <div
        onClick={() => {
          setSelectedComment(comment);
          if (comment.status === 'unread') updateStatus(comment.id, 'read');
        }}
        className={`p-4 cursor-pointer transition-all relative hover:bg-gray-50/80 border-l-4 ${
          isSelected
            ? 'bg-blue-50/40 border-l-blue-500'
            : comment.status === 'unread'
              ? 'border-l-blue-300 bg-blue-50/10'
              : 'border-l-transparent'
        }`}
      >
        <div className="flex justify-between items-start mb-1.5">
          <div className="flex items-center gap-2.5">
            {comment.author.avatar ? (
              <img src={comment.author.avatar} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-blue-500 flex items-center justify-center text-white font-bold text-sm shadow">
                {comment.author.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-sm text-gray-900 truncate max-w-[130px]">
                  {comment.author.name}
                </span>
                {comment.platform === 'facebook'
                  ? <Facebook size={12} className="text-blue-600 flex-shrink-0" />
                  : <Instagram size={12} className="text-pink-500 flex-shrink-0" />}
              </div>
              <span className="text-[11px] text-gray-400">{comment.timeAgo}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            {comment.status === 'flagged' && <Flag size={13} className="text-red-500 fill-red-100" />}
            {comment.status === 'unread'  && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
            <span className="text-sm" title={comment.sentiment}>{getSentimentEmoji(comment.sentiment)}</span>
          </div>
        </div>

        <p className="text-sm text-gray-600 line-clamp-2 ml-11">{comment.text}</p>

        {showPostInfo && comment.post?.title && (
          <div className="mt-2.5 ml-11 p-1.5 bg-gray-50 border border-gray-100 rounded-lg flex items-center gap-2 max-w-full">
            {comment.post.image ? (
              <img src={comment.post.image} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded bg-gray-100 flex items-center justify-center text-gray-400 flex-shrink-0">
                <FileText size={12} />
              </div>
            )}
            <span className="text-[10px] text-gray-500 font-medium truncate flex-1">
              {comment.post.title}
            </span>
          </div>
        )}

        {comment.replies?.length > 0 && (
          <div className="mt-2 ml-11 text-xs text-green-600 font-medium flex items-center gap-1">
            <CheckCircle2 size={12} /> Replied
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-theme(spacing.20))] max-w-7xl mx-auto p-4 md:p-6 bg-gray-50/50">

      {/* ── Top Bar ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-5 gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center gap-2">
            <Inbox size={26} className="text-blue-600" />
            Social Inbox
            {unreadCount > 0 && (
              <span className="text-sm font-semibold bg-blue-600 text-white px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage and reply to comments from your social posts</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Group Toggle */}
          <button
            onClick={() => setGroupByPost(!groupByPost)}
            className={`px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all ${
              groupByPost
                ? 'bg-blue-600 text-white border-blue-600 shadow'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            Group by Post
          </button>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search comments…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm w-52"
            />
          </div>

          {/* Platform filter */}
          <div className="flex bg-white border border-gray-200 rounded-lg p-0.5 gap-0.5">
            {['all', 'facebook', 'instagram'].map(p => (
              <button
                key={p}
                onClick={() => setPlatform(p)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${
                  platform === p ? 'bg-indigo-50 text-indigo-600' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                {p === 'facebook' ? <Facebook size={14} className="inline mr-1" /> :
                 p === 'instagram' ? <Instagram size={14} className="inline mr-1" /> : null}
                {p}
              </button>
            ))}
          </div>

          {/* Status filter */}
          <div className="flex bg-white border border-gray-200 rounded-lg p-0.5 gap-0.5">
            {['all', 'unread', 'flagged'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${
                  filter === f ? 'bg-indigo-50 text-indigo-600' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                {f}
                {f === 'unread' && unreadCount > 0 && (
                  <span className="ml-1 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-[10px]">
                    {unreadCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Sync button */}
          <button
            onClick={handleSyncComments}
            disabled={syncing}
            title="Pull latest comments from Facebook"
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all ${
              syncing
                ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-wait'
                : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50 hover:shadow-sm'
            }`}
          >
            <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing…' : 'Sync'}
          </button>
        </div>
      </div>

      {/* ── Main Panel ── */}
      <div className="flex flex-col md:flex-row flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden min-h-0">

        {/* Left: Comment list */}
        <div className={`w-full md:w-2/5 flex flex-col border-r border-gray-100 ${selectedComment ? 'hidden md:flex' : 'flex'}`}>
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between font-medium">
            <span className="text-sm text-gray-700">
              {filteredComments.length} conversation{filteredComments.length !== 1 ? 's' : ''}
            </span>
            {groupByPost && (
              <span className="text-[10px] bg-blue-50 text-blue-700 font-semibold px-2 py-0.5 rounded-full">
                Grouped View
              </span>
            )}
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
            {loading ? (
              <div className="flex items-center justify-center h-full py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : filteredComments.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-16 px-6">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <MessageCircle size={28} className="text-gray-300" />
                </div>
                <p className="font-medium text-gray-600">No comments yet</p>
                <p className="text-sm text-gray-400 mt-1">Click <strong>Sync</strong> to pull comments from Facebook</p>
              </div>
            ) : groupByPost ? (
              groupedComments.map(group => (
                <div key={group.post.id} className="bg-gray-50/20">
                  <div className="sticky top-0 z-10 px-4 py-2.5 bg-gray-100/80 backdrop-blur border-b border-gray-200 flex items-center gap-2 shadow-sm">
                    {group.post.image ? (
                      <img src={group.post.image} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0 ring-1 ring-black/10" />
                    ) : (
                      <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center text-gray-400 flex-shrink-0">
                        <FileText size={12} />
                      </div>
                    )}
                    <span className="text-[11px] font-bold text-gray-700 truncate flex-1">
                      {group.post.title || 'Unlinked Post'}
                    </span>
                    <span className="text-[10px] font-bold bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                      {group.comments.length}
                    </span>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {group.comments.map(comment => (
                      <CommentCard key={comment.id} comment={comment} showPostInfo={false} />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              filteredComments.map(comment => (
                <CommentCard key={comment.id} comment={comment} showPostInfo={true} />
              ))
            )}
          </div>
        </div>

        {/* Right: Conversation detail */}
        <div className={`w-full md:w-3/5 flex flex-col bg-white ${!selectedComment ? 'hidden md:flex' : 'flex'}`}>
          {!selectedComment ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-gray-50/30">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mb-5 shadow-inner">
                <MessageCircle size={40} className="text-indigo-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-gray-700">Select a comment</h3>
              <p className="text-sm text-gray-400 mt-1 max-w-xs">
                Choose a conversation from the left panel to view and reply
              </p>
            </div>
          ) : (
            <>
              {/* Detail Header */}
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10 shadow-sm">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSelectedComment(null)}
                    className="md:hidden p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg mr-1"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  {selectedComment.author.avatar ? (
                    <img src={selectedComment.author.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-blue-500 flex items-center justify-center text-white font-bold text-sm">
                      {selectedComment.author.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{selectedComment.author.name}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {selectedComment.platform === 'facebook'
                        ? <><Facebook size={11} className="text-blue-600" /><span className="text-xs text-gray-400">Facebook</span></>
                        : <><Instagram size={11} className="text-pink-500" /><span className="text-xs text-gray-400">Instagram</span></>}
                      <span className={`text-xs font-medium ${getSentimentColor(selectedComment.sentiment)}`}>
                        · {getSentimentEmoji(selectedComment.sentiment)} {selectedComment.sentiment}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <ActionBtn
                    onClick={() => updateStatus(selectedComment.id, 'read')}
                    title="Mark read"
                    active={selectedComment.status === 'read'}
                    activeClass="text-green-600 bg-green-50"
                    hoverClass="hover:text-green-600 hover:bg-green-50"
                  >
                    <CheckCircle2 size={17} />
                  </ActionBtn>
                  <ActionBtn
                    onClick={() => updateStatus(selectedComment.id, selectedComment.status === 'flagged' ? 'read' : 'flagged')}
                    title="Flag"
                    active={selectedComment.status === 'flagged'}
                    activeClass="text-red-600 bg-red-50"
                    hoverClass="hover:text-red-600 hover:bg-red-50"
                  >
                    <Flag size={17} className={selectedComment.status === 'flagged' ? 'fill-current' : ''} />
                  </ActionBtn>
                  <ActionBtn
                    onClick={() => updateStatus(selectedComment.id, 'archived')}
                    title="Archive"
                    activeClass=""
                    hoverClass="hover:text-gray-900 hover:bg-gray-100"
                  >
                    <Archive size={17} />
                  </ActionBtn>
                </div>
              </div>

              {/* Thread */}
              <div className="flex-1 overflow-y-auto p-5 space-y-5 bg-gray-50/30">
                {/* Post reference */}
                {selectedComment.post?.title && (
                  <div className="border border-indigo-100 rounded-xl p-3.5 bg-gradient-to-r from-blue-50/40 to-indigo-50/40 shadow-sm flex gap-3.5 items-center">
                    {selectedComment.post.image ? (
                      <img src={selectedComment.post.image} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0 ring-1 ring-black/10" />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-white flex items-center justify-center text-gray-400 flex-shrink-0 shadow-sm">
                        <FileText size={20} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-0.5">Parent Social Post</p>
                      <p className="text-xs font-semibold text-gray-800 line-clamp-2 leading-relaxed">{selectedComment.post.title}</p>
                    </div>
                  </div>
                )}

                {/* Original comment bubble */}
                <div className="flex gap-3">
                  {selectedComment.author.avatar ? (
                    <img src={selectedComment.author.avatar} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-gray-600 font-bold text-sm flex-shrink-0">
                      {selectedComment.author.name.charAt(0)}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="bg-white border border-gray-100 p-4 rounded-2xl rounded-tl-sm shadow-sm">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold text-sm text-gray-900">{selectedComment.author.name}</span>
                        <span className="text-[11px] text-gray-400">
                          {new Date(selectedComment.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{selectedComment.text}</p>
                    </div>
                  </div>
                </div>

                {/* Reply bubbles */}
                {(selectedComment.replies || []).map(reply => (
                  <div key={reply.id} className="flex gap-3 flex-row-reverse">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow">
                      A
                    </div>
                    <div className="flex-1 flex justify-end">
                      <div className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white p-4 rounded-2xl rounded-tr-sm shadow-sm max-w-[85%]">
                        <div className="flex justify-between items-center mb-2 gap-4">
                          <span className="font-semibold text-sm">{reply.author}</span>
                          <span className="text-[11px] text-blue-200">
                            {new Date(reply.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{reply.text}</p>
                      </div>
                    </div>
                  </div>
                ))}

                <div ref={messagesEndRef} />
              </div>

              {/* Reply box */}
              <div className="p-4 bg-white border-t border-gray-100 shadow-[0_-2px_10px_rgba(0,0,0,0.04)]">
                <div className="flex gap-3 items-start">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0 mt-1 shadow">
                    A
                  </div>
                  <div className="flex-1 relative">
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleReply(); }}
                      placeholder={`Reply to ${selectedComment.author.name}… (Ctrl+Enter to send)`}
                      maxLength={500}
                      rows={3}
                      className="w-full border border-gray-200 rounded-xl p-3 pr-24 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm resize-none transition-shadow"
                    />
                    <div className="absolute right-2 bottom-2 flex items-center gap-2">
                      <span className={`text-xs font-mono ${replyText.length > 450 ? 'text-red-500' : 'text-gray-400'}`}>
                        {replyText.length}/500
                      </span>
                      <button
                        onClick={handleReply}
                        disabled={!replyText.trim() || sending}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          !replyText.trim() || sending
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-md hover:shadow-blue-200 active:scale-95'
                        }`}
                      >
                        {sending
                          ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          : <Send size={14} />}
                        {sending ? 'Sending' : 'Reply'}
                      </button>
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mt-2 ml-11">
                  Replies are posted directly to {selectedComment.platform === 'facebook' ? 'Facebook' : 'Instagram'} via the API
                </p>
                {replyError && (
                  <div className="mt-3 ml-11 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                    <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-red-700">Reply failed</p>
                      <p className="text-xs text-red-600 mt-0.5">{replyError}</p>
                      {replyError.includes('Permission') || replyError.includes('permission') ? (
                        <a
                          href="/admin/social-media"
                          className="text-xs text-red-700 font-semibold underline mt-1 inline-block hover:text-red-900"
                        >
                          → Go to Social Media → Disconnect & Reconnect Facebook
                        </a>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* Small reusable action button */
const ActionBtn = ({ children, onClick, title, active, activeClass, hoverClass }) => (
  <button
    onClick={onClick}
    title={title}
    className={`p-2 rounded-lg transition-colors text-gray-500 ${active ? activeClass : hoverClass}`}
  >
    {children}
  </button>
);

/* ChevronLeft inline so we don't need another import */
const ChevronLeft = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

export default SocialMediaComments;
